import { AlternativeCurrenciesTypes } from './../car/shared/models/alternative-currency/alternative-currencies-types.enum';
import { CreditCardType } from './credit-card-type';
import { Injectable } from '@angular/core';
import { AlternativeCurrenciesVendors } from '../car/shared/models/alternative-currency/alternative-currencies-vendors.enum';
import { Platform } from '../shared/enums/platform.enum';

export const ALLOWED_POINTS_VENDORS = [AlternativeCurrenciesVendors.Hicv, AlternativeCurrenciesVendors.Rci, AlternativeCurrenciesVendors.Dri];

export enum PointsModel {
  spend = 'spend',
  rebate = 'rebate'
}

export class CarRedemptionOptions {
  price_tier_1_definition: string;
  reimbursement_conversion_rate: number;
  redemption_conversion_rate: number;
  minimum: number;
  maximum: number;
  can_cancel: boolean;
  can_refund: boolean;
  refund_policy: string;
  policy_override: boolean;
  points_model: PointsModel;
  online_usage_fee: number;
  online_usage_fee_label: string;
  offline_usage_fee: number;
  offline_usage_fee_label: string;
  guest_online_usage_fee: number;
  guest_online_usage_fee_label: string;
  guest_offline_usage_fee: number;
  guest_offline_usage_fee_label: string;
  guest_fee_check_box_label: string;
  weight: number;
  car_fee_per_item: string;
  fee_agent_override: boolean;
  points_apply_percentage: number;
  apply_to_base_rate: boolean;
  apply_to_fees: boolean;
  apply_to_taxes: boolean;
  allow_partial_point_apply: boolean;
  usage_fee: number;
  usage_fee_cap: number;
  usage_fee_label: string;
  overcap_usage: number;
  overcap_usage_fee: number;
  overcap_usage_fee_label: string;
  skip_points_verify: boolean;
}

export class MemberHardCurrency {
  skip_points_verify: boolean;
  private carRedemptionOptions: CarRedemptionOptions;
  id: string;
  private alternativeCurrenciesType: AlternativeCurrenciesTypes;
  sub_type: string;
  label: string;
  vendor: string;
  car_model: PointsModel;
  redeem_for_car_online: boolean;
  redeem_for_car: boolean;
  conversion_rate: number;
  car_min: number;
  car_max: number;
  balance: number;
  can_refund: boolean;

  public get car_redemption_options(): CarRedemptionOptions {
    return this.carRedemptionOptions;
  }

  public get pointsApplyPercentage(): number {
      return this.carRedemptionOptions &&
          this.carRedemptionOptions.points_apply_percentage;
  }

  public get carRedemptionConversionRate(): number {
    return this.carRedemptionOptions &&
      this.carRedemptionOptions.redemption_conversion_rate;
  }

  public get carFeeAgentOverride() {
    return this.carRedemptionOptions &&
      this.carRedemptionOptions.fee_agent_override;
  }

  public get isHicv() {
    return this.vendor === AlternativeCurrenciesVendors.Hicv;
  }

  public isGuestFeeAllowed(isAgent: boolean): boolean {
    return !!this.guestFee(isAgent);
  }

  public guestFee(isAgent: boolean): { description: string, amount: number } {
    const description = isAgent ?
      (this.carRedemptionOptions
        && this.carRedemptionOptions.guest_offline_usage_fee_label)
      : (this.carRedemptionOptions
        && this.carRedemptionOptions.guest_online_usage_fee_label);
    const amount = isAgent ?
      (this.carRedemptionOptions
        && this.carRedemptionOptions.guest_offline_usage_fee)
      : (this.carRedemptionOptions
        && this.carRedemptionOptions.guest_online_usage_fee);

    return amount && description && { description: description, amount: amount };
  }

  public processingFee(isAgent: boolean): { description: string, amount: number } {
    const description = isAgent ?
      (this.carRedemptionOptions
        && this.carRedemptionOptions.offline_usage_fee_label)
      : (this.carRedemptionOptions
        && this.carRedemptionOptions.online_usage_fee_label);
    const amount = isAgent ?
      (this.carRedemptionOptions
        && this.carRedemptionOptions.offline_usage_fee)
      : (this.carRedemptionOptions
        && this.carRedemptionOptions.online_usage_fee);

    return (typeof amount === 'number') && description && { description: description, amount: amount };
  }

  public vendorOf(vendorList: AlternativeCurrenciesVendors[]): boolean {
    return vendorList.findIndex(v => v === this.vendor) > -1;
  }

  public set type(value: AlternativeCurrenciesTypes | string | undefined) {
    if (typeof value === 'string' && value) {
      switch (value.toLowerCase()) {
        case AlternativeCurrenciesTypes.Points.toLowerCase():
          this.alternativeCurrenciesType = AlternativeCurrenciesTypes.Points;
          break;
        case AlternativeCurrenciesTypes.VacationCash.toLowerCase():
          this.alternativeCurrenciesType = AlternativeCurrenciesTypes.VacationCash;
          break;
        case AlternativeCurrenciesTypes.Loyalty.toLowerCase():
          this.alternativeCurrenciesType = AlternativeCurrenciesTypes.Loyalty;
          break;
      }
    }
  }

  public get type(): AlternativeCurrenciesTypes | /** @impossible */ string | undefined  {
    return this.alternativeCurrenciesType;
  }

  public set car_redemption_options(value: CarRedemptionOptions) {
    if (!value) {
      return;
    }
    this.carRedemptionOptions = Object.assign(new CarRedemptionOptions(), value);
  }

  public isPointsSpendAllowed(isAgent: boolean): boolean {

    const isSpendModelAcceptable = this.car_redemption_options && ( this.car_redemption_options.points_model === PointsModel.spend || this.car_model === PointsModel.spend);
    const result = isSpendModelAcceptable
        && this.type
        && this.type.toLowerCase() === AlternativeCurrenciesTypes.Points
        && this.isReedemForCar(isAgent) && this.carRedemptionConversionRate > 0;

    return result;
  }

  public isLoyaltyAllowed(isAgent: boolean): boolean {

    const isLoyaltyModelAcceptable = this.car_redemption_options;
    const result = isLoyaltyModelAcceptable
      && this.type
      && this.type.toLowerCase() === AlternativeCurrenciesTypes.Loyalty
      && this.isReedemForCar(isAgent) && this.carRedemptionConversionRate > 0;

    return result;
  }

  private isReedemForCar(isAgent: boolean) {
    const redeemForCarOnline = !isAgent && this.redeem_for_car_online;
    const redeemForCar = !!isAgent && this.redeem_for_car;
    return redeemForCar || redeemForCarOnline;
  }

  public isSpendAllowed(
    isAgent: boolean,
    bookingTotal: number = null,
    balance: number = null): boolean {
    return this.conversion_rate > 0
      && this.car_model === PointsModel.spend
      && this.isReedemForCar(isAgent)
      && ((this.car_min > 0
        && (!bookingTotal
          || bookingTotal > Math.ceil(this.car_min * this.conversion_rate)))
        || !this.car_min)
      && ((this.car_min > 0
        && (!balance
          || balance > Math.ceil(this.car_min * this.conversion_rate))) 
        || !this.car_min);
  }
}



export class MemberMarginCurrency {
  id: string;
  private alternativeCurrenciesType: AlternativeCurrenciesTypes;
  label: string;
  conversion_rate: number = 1;
  balance: number;
  must_use_max_on_each_transaction: boolean;
  can_refund: boolean;
  show_refund_disclaimer: boolean;
  refund_disclaimer: string;
  redeem_for_car: boolean;

  public set type(value: AlternativeCurrenciesTypes | string | undefined) {
    if (typeof value === 'string' && value) {
      switch (value.toLowerCase()) {
        case "db":
        case "dbc":
          this.alternativeCurrenciesType = AlternativeCurrenciesTypes.DBC;
          break;
      }
    }
  }

  public get type(): AlternativeCurrenciesTypes | /** @impossible */ string | undefined {
    return this.alternativeCurrenciesType;
  }

  public isMarginCurrencyAllowed(type: AlternativeCurrenciesTypes, isAgent: boolean): boolean
  {
    const result =
      this.type.toLowerCase().startsWith(type) && this.redeem_for_car;

    return result;
  }

  public isMarginCurrencyAllowedByString(type: string)
  {
    const result =
      this.type.toLowerCase().startsWith(type) && this.redeem_for_car;

    return result;
  }
}

export class MemberAddress {
  city: string
  country: string
  is_primary: boolean
  label: string
  postal_code: string
  state_province: string
  street1: string
  street2: string
  street3: string
}

export class MemberEmailAddress {
  address: string
  is_primary: boolean
  label: string
}

export class MemberPhoneNumber {
  isPrimary: boolean
  label: string
  number: string
}

export class MemberInfo {
  expiration_date: Date;
  member_id: string;

  private alternativeHardCurrencies: MemberHardCurrency[] = [];
  private alternativeMarginCurrencies : MemberMarginCurrency[] = [];
  private pointsCurrencyCache: MemberHardCurrency;
  private marginCurrencyCache: MemberMarginCurrency;
  private loyaltyCurrencyCache: MemberHardCurrency;
  private pointsSubType: string;
  private club_url: string;
  private _addresses = [];
  private _email_addresses = [];
  private _phone_numbers = [];

  public get phone_numbers(): MemberPhoneNumber[] {
    return this._phone_numbers;
  }

  public set phone_numbers(value: MemberPhoneNumber[]) {
    if (!value) {
      this.phone_numbers = null;
      return;
    }
    this._phone_numbers = [];

    value.forEach(v => {
      this._phone_numbers.push(Object.assign(new MemberPhoneNumber(), v));
    });
  }

  public get email_addresses(): MemberEmailAddress[] {
    return this._email_addresses;
  }

  public get clubUrl(): string {
    return this.club_url;
  }

  public set email_addresses(value: MemberEmailAddress[]) {
    if (!value) {
      this.email_addresses = null;
      return;
    }
    this._email_addresses = [];

    value.forEach(v => {
      this._email_addresses.push(Object.assign(new MemberEmailAddress(), v));
    });
  }

  public get addresses(): MemberAddress[] {
    return this._addresses;
  }

  public set addresses(value: MemberAddress[]) {
    if (!value) {
      this.addresses = null;
      return;
    }
    this._addresses = [];

    value.forEach(v => {
      this._addresses.push(Object.assign(new MemberAddress(), v));
    });
  }

  public get alternative_hard_currencies(): MemberHardCurrency[] {
    return this.alternativeHardCurrencies;
  }

  public set alternative_hard_currencies(value: MemberHardCurrency[]) {
    if (!value) {
      this.alternative_hard_currencies = null;
      return;
    }
    this.alternativeHardCurrencies = [];
    value.forEach(v => {
      this.alternativeHardCurrencies.push(Object.assign(new MemberHardCurrency(), v));
    });
  }

  public get alternative_margin_currencies(): MemberMarginCurrency[] {
    return this.alternativeMarginCurrencies;
  }

  public set alternative_margin_currencies(value: MemberMarginCurrency[]) {
    if (!value) {
      this.alternative_margin_currencies = null;
      return;
    }
    this.alternativeMarginCurrencies = [];

    value.forEach(v => {
      this.alternativeMarginCurrencies.push(Object.assign(new MemberMarginCurrency(), v));
    });
  }


  public set sub_type(value: string) {
    this.pointsSubType = value;
  }

  public getAlternativeCurrency(
    type: AlternativeCurrenciesTypes,
    vendor?: string): MemberHardCurrency {

    if (!this.alternativeHardCurrencies) {
      return null;
    }

    if (this.pointsSubType === undefined) {
      const alternativeCurrency: MemberHardCurrency = this.alternativeHardCurrencies.find(c => c.type && type && c.type === type);
      return alternativeCurrency;
    } else {
      const alternativeCurrency: MemberHardCurrency = this.alternativeHardCurrencies.find(c => c.sub_type && c.sub_type.toLowerCase() === this.pointsSubType.toLowerCase());
      return alternativeCurrency;
    }
  }

  public getAlternativeMarginCurrency(type: AlternativeCurrenciesTypes): MemberMarginCurrency {

    if (!this.alternativeMarginCurrencies) {
      return null;
    }

    if (type == AlternativeCurrenciesTypes.DBC) {
      const alternativeCurrency = this.alternativeMarginCurrencies.find(c => c.type === type);
      return alternativeCurrency;
    } else {
      return undefined;
    }

  }

  //Third Party Points
  public get pointsCurrency(): MemberHardCurrency {
    return this.pointsCurrencyCache
      ? this.pointsCurrencyCache
      : this.pointsCurrencyCache = this.getAlternativeCurrency(AlternativeCurrenciesTypes.Points);
  }

  //DBC
  public get marginCurrency(): MemberMarginCurrency {
    return this.marginCurrencyCache
      ? this.marginCurrencyCache
      : this.marginCurrencyCache = this.getAlternativeMarginCurrency(AlternativeCurrenciesTypes.DBC);
  }

  //Loyalty Points
  public get loyaltyCurrency(): MemberHardCurrency {

    const loyalty: MemberHardCurrency = this.loyaltyCurrencyCache
      ? this.loyaltyCurrencyCache
      : this.loyaltyCurrencyCache = this.getAlternativeCurrency(AlternativeCurrenciesTypes.Loyalty);
    return loyalty;
  }

  public updateAltCurrencyWith(changedCurrency: MemberHardCurrency)
    : MemberHardCurrency {
    const index = this.alternativeHardCurrencies && this.alternativeHardCurrencies.findIndex(ac => ac.type
      && ac.type === changedCurrency.type && ac.sub_type === changedCurrency.sub_type);

    if (index > -1) {
      if (this.alternativeHardCurrencies[index].type === AlternativeCurrenciesTypes.Points) {
        this.pointsCurrencyCache = null;
      }
      if (this.alternativeHardCurrencies[index].type === AlternativeCurrenciesTypes.Loyalty) {
        this.loyaltyCurrencyCache = null;
      }
      this.alternativeHardCurrencies[index] = Object.assign(new MemberHardCurrency(), changedCurrency);

      return this.alternativeHardCurrencies[index];
    }
  }

  public updateMarginCurrencyWith(changedCurrency: MemberMarginCurrency): MemberMarginCurrency {
    const index = this.alternativeMarginCurrencies && this.alternativeMarginCurrencies.findIndex(ac => ac.type && ac.type === changedCurrency.type);

    if (index > -1) {
      if (this.alternativeMarginCurrencies[index].type === AlternativeCurrenciesTypes.DBC) {
        this.marginCurrencyCache = null;
      }
      this.alternativeMarginCurrencies[index] = Object.assign(new MemberMarginCurrency(), changedCurrency);

      return this.alternativeMarginCurrencies[index];
    }
  }

  public getPrimaryAddress(): MemberAddress {
    return this.getPrimaryItem(this.addresses, item => item.is_primary);
  }

  public getPrimaryPhone(): MemberPhoneNumber {
    return this.getPrimaryItem(this.phone_numbers, item => item.isPrimary);
  }

  public getPrimaryEmail(): MemberEmailAddress {
    return this.getPrimaryItem(this.email_addresses, item => item.is_primary);
  }

  private getPrimaryItem<T>(array: T[], isPrimary: (item: T) => boolean): any {
    if (!array || array.length == 0) {
      return null;
    }

    for (var i = 0; i < array.length; i++) {
      if (isPrimary(array[i])) {
        return array[i];
      }
    }

    // in case none was primary:
    return array[0];
  }
}

export class Identity {
  agent_mode: boolean;
  ice_member_class_id: string;
  ice_member_class_name: string;
  ice_agent_name: string;
}

export enum GlobalErrorReason {
  Unknown = 0,
  Unauthorized = 401,
  InternalServerError = 500
}

export interface ApiEndpoint {
  name: string;
  url: string;
}

export interface Accertify {
  enabled: boolean;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class SessionData {
  constructor(
    public correlationId?: string,
    public memberId?: string,
    public thirdPartyId?: string,
    public thirdPartyGuid?: string,
    public transactionType?: string, 
    public agentId?: string,
    public partnerId?: string,
    public nameId?: string,
    public token?: string,
    public templatesUrls?: string[], 
    public stylesheetsUrls?: string[],
    public endpoints?: ApiEndpoint[],
    public accertify?: Accertify,
    public fallbackUrl?: string,
    public googleApiKey?: string,
    public isAgent?: boolean,
    public globalErrorTriggered?: boolean,
    public acceptedCards?: CreditCardType[],
    public memberInfo?: MemberInfo,
    public globalErrorReason?: GlobalErrorReason,
    public identity?: Identity,
    public optionsId?: string,
    public loginToken?: string,
    public isAuthenticated?: boolean,
    public transactionId?: string,
    public platform?: string,
    public memberclassid?: string
  ) { }

  private API_URL_KEY = 'WebApi';
  private apiUrlValue: string;

  public getUrl(key: string): string {
    const value = key && this.endpoints
      && this.endpoints.find(e => e.name === key);
    return value && value.url;
  }

  public get apiUrl(): string {

    if (!this.apiUrlValue) {
      const value = this.endpoints
        && this.endpoints.find(e => e.name === this.API_URL_KEY);
      this.apiUrlValue = value && value.url;
    }

    return this.apiUrlValue;
  }
  
  public getPlatform(): string {
    if (this.optionsId != null &&
      this.optionsId.startsWith("c1:e3:"))
      return Platform.SOR;

    return Platform.ICE;
  }

}

